/**
 * MakeoverDeliverablesSection — four deliverable cards showing exactly
 * what the ₹999 Full Profile Makeover unlocks: Résumé makeover,
 * LinkedIn profile makeover, Peer profile benchmarking, LinkedIn
 * content support.
 *
 * Desktop: 4-up grid. Mobile: horizontal snap carousel (one card per
 * swipe) so the deliverable list stays scannable on small screens.
 *
 * Each card leads with a lightweight inline mockup so the carousel
 * sells visually, not just verbally.
 */

import React from 'react';
import { motion } from 'framer-motion';
import {
  FileText,
  Linkedin,
  BarChart3,
  PenTool,
  Check,
  ArrowRight,
  Heart,
  MessageCircle,
  Repeat2,
  Send,
  Star,
  TrendingUp,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  fadeUp,
  kicker,
  displaySection,
  sectionWrap,
  sectionInner,
} from './shared';

/* -------------------------------------------------------------------------
 * Inline mockups — one per deliverable. Kept as tiny components so the
 * carousel ships zero external images.
 * ----------------------------------------------------------------------- */

const ResumeMockup = () => (
  <div className="relative aspect-[16/10] rounded-2xl bg-background border border-border/70 shadow-[0_12px_40px_-20px_hsl(220_40%_2%/0.25)] overflow-hidden">
    {/* Header strip */}
    <div className="h-[18%] bg-gradient-to-r from-sky-500/90 to-indigo-500/90 flex items-center px-4">
      <div>
        <div className="h-1.5 w-20 bg-white/90 rounded" />
        <div className="mt-1 h-1 w-14 bg-white/60 rounded" />
      </div>
    </div>
    {/* Body: section title + bullet rows */}
    <div className="p-4 space-y-3">
      <div className="h-1.5 w-14 bg-sky-500/80 rounded" />
      <div className="space-y-1.5">
        <div className="h-1 w-full bg-foreground/15 rounded" />
        <div className="h-1 w-[88%] bg-foreground/15 rounded" />
        <div className="h-1 w-[72%] bg-foreground/15 rounded" />
      </div>
      <div className="h-1.5 w-10 bg-sky-500/80 rounded mt-3" />
      <div className="space-y-1.5">
        <div className="h-1 w-[94%] bg-foreground/15 rounded" />
        <div className="h-1 w-[80%] bg-foreground/15 rounded" />
      </div>
    </div>
    {/* "ATS pass" chip */}
    <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 text-[9.5px] font-mono-edge tracking-[0.14em] uppercase bg-emerald-500/15 text-emerald-700 px-2 py-0.5 rounded-full">
      <Check size={9} strokeWidth={3} aria-hidden="true" /> ATS pass
    </span>
  </div>
);

const LinkedInMockup = () => (
  <div className="relative aspect-[16/10] rounded-2xl bg-background border border-border/70 shadow-[0_12px_40px_-20px_hsl(220_40%_2%/0.25)] overflow-hidden">
    {/* Banner */}
    <div className="h-[32%] bg-gradient-to-br from-indigo-400/60 to-sky-400/50" />
    {/* Avatar overhang + name block */}
    <div className="px-4 -mt-6 flex items-start gap-3">
      <div className="h-11 w-11 rounded-full bg-indigo-500/90 ring-4 ring-background text-white text-[11px] font-semibold inline-flex items-center justify-center shadow-sm">
        AS
      </div>
      <div className="pt-7">
        <div className="h-1.5 w-20 bg-foreground/80 rounded" />
        <div className="mt-1 h-1 w-28 bg-foreground/35 rounded" />
      </div>
    </div>
    {/* Headline/about preview */}
    <div className="px-4 pt-3 space-y-1.5">
      <div className="h-1 w-[88%] bg-foreground/20 rounded" />
      <div className="h-1 w-[72%] bg-foreground/20 rounded" />
    </div>
    {/* "in" badge */}
    <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[9.5px] font-mono-edge font-bold text-white bg-[#0A66C2] px-1.5 py-0.5 rounded">
      in
    </span>
  </div>
);

const BenchmarkMockup = () => (
  <div className="relative aspect-[16/10] rounded-2xl bg-background border border-border/70 shadow-[0_12px_40px_-20px_hsl(220_40%_2%/0.25)] overflow-hidden p-4">
    <div className="flex items-center justify-between">
      <div>
        <div className="h-1.5 w-16 bg-foreground/70 rounded" />
        <div className="mt-1 h-1 w-10 bg-emerald-500/70 rounded" />
      </div>
      <TrendingUp size={14} className="text-emerald-600" aria-hidden="true" />
    </div>
    {/* Mini bar comparison: You vs Peers across 4 dimensions */}
    <div className="mt-3 space-y-2.5">
      {[
        { label: 'Headline', you: 92, peer: 68 },
        { label: 'About', you: 86, peer: 71 },
        { label: 'Skills', you: 78, peer: 74 },
        { label: 'Posts', you: 64, peer: 55 },
      ].map((row) => (
        <div key={row.label}>
          <div className="flex items-center justify-between">
            <div className="h-1 w-10 bg-foreground/40 rounded" />
            <div className="text-[8.5px] font-mono-edge tracking-widest text-muted-foreground">
              {row.you}
            </div>
          </div>
          <div className="mt-1 relative h-2 w-full bg-muted/50 rounded-full overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-emerald-500/80 rounded-full"
              style={{ width: `${row.you}%` }}
            />
            <div
              className="absolute inset-y-0 left-0 border-r-2 border-rose-400/80"
              style={{ width: `${row.peer}%` }}
            />
          </div>
        </div>
      ))}
    </div>
    <div className="mt-3 flex items-center gap-3 text-[9px] text-muted-foreground">
      <span className="inline-flex items-center gap-1">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> You
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="h-1.5 w-1.5 rounded-full bg-rose-400" /> Peer median
      </span>
    </div>
  </div>
);

const PostMockup = () => (
  <div className="relative aspect-[16/10] rounded-2xl bg-background border border-border/70 shadow-[0_12px_40px_-20px_hsl(220_40%_2%/0.25)] overflow-hidden">
    <div className="p-4">
      {/* Author row */}
      <div className="flex items-start gap-2.5">
        <div className="h-8 w-8 rounded-full bg-rose-500/90 text-white text-[10px] font-semibold inline-flex items-center justify-center shrink-0">
          AS
        </div>
        <div className="min-w-0 flex-1">
          <div className="h-1.5 w-20 bg-foreground/80 rounded" />
          <div className="mt-1 h-1 w-24 bg-foreground/30 rounded" />
          <div className="mt-1 h-[7px] w-14 bg-foreground/20 rounded" />
        </div>
      </div>
      {/* Post copy */}
      <div className="mt-3 space-y-1.5">
        <div className="h-1 w-full bg-foreground/25 rounded" />
        <div className="h-1 w-[92%] bg-foreground/25 rounded" />
        <div className="h-1 w-[76%] bg-foreground/25 rounded" />
      </div>
      {/* Reactions */}
      <div className="mt-3 flex items-center gap-3 text-rose-500/80">
        <span className="flex items-center gap-1 text-[9px]">
          <Heart size={10} strokeWidth={2.5} aria-hidden="true" fill="currentColor" />
          <span className="text-foreground/60">214</span>
        </span>
        <span className="flex items-center gap-1 text-[9px] text-sky-500/80">
          <MessageCircle size={10} strokeWidth={2.5} aria-hidden="true" />
          <span className="text-foreground/60">38</span>
        </span>
        <span className="flex items-center gap-1 text-[9px] text-emerald-500/80">
          <Repeat2 size={10} strokeWidth={2.5} aria-hidden="true" />
          <span className="text-foreground/60">11</span>
        </span>
        <span className="flex items-center gap-1 text-[9px] text-indigo-500/80 ml-auto">
          <Send size={10} strokeWidth={2.5} aria-hidden="true" />
        </span>
      </div>
    </div>
    <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[9.5px] font-mono-edge tracking-[0.14em] uppercase bg-rose-500/15 text-rose-700 px-2 py-0.5 rounded-full">
      <Star size={9} strokeWidth={2.6} aria-hidden="true" /> Draft 3 / 10
    </span>
  </div>
);

const DELIVERABLES = [
  {
    icon: FileText,
    title: 'Résumé makeover',
    body: 'Give your experience a clearer structure and sharper language.',
    tone: 'from-sky-500/12 to-transparent',
    accent: 'text-sky-600',
    ring: 'border-sky-200/60',
    Mockup: ResumeMockup,
    bullets: [
      'Stronger professional language',
      'Clearer experience and achievement statements',
      'Better presentation of your skills',
      'High skill/keyword match and ATS compliant',
    ],
    testid: 'deliverable-resume',
  },
  {
    icon: Linkedin,
    title: 'LinkedIn profile makeover',
    body:
      'Make your profile easier to understand and more relevant to your next opportunity.',
    tone: 'from-indigo-500/12 to-transparent',
    accent: 'text-indigo-600',
    ring: 'border-indigo-200/60',
    Mockup: LinkedInMockup,
    bullets: [
      'A headline that communicates your value',
      'An About section that tells your professional story',
      'Rewritten Experience sections',
    ],
    testid: 'deliverable-linkedin',
  },
  {
    icon: BarChart3,
    title: 'Peer profile benchmarking',
    body:
      'Understand how your profile compares with a relevant sample of benchmarked professionals.',
    tone: 'from-emerald-500/12 to-transparent',
    accent: 'text-emerald-600',
    ring: 'border-emerald-200/60',
    Mockup: BenchmarkMockup,
    bullets: [
      'Identify strengths and gaps in your positioning',
      'See where your profile could communicate more clearly',
      'Receive a downloadable PDF and PPT report',
    ],
    testid: 'deliverable-benchmark',
  },
  {
    icon: PenTool,
    title: 'LinkedIn content support',
    body: 'Give people more reasons to notice your expertise.',
    tone: 'from-rose-500/12 to-transparent',
    accent: 'text-rose-600',
    ring: 'border-rose-200/60',
    Mockup: PostMockup,
    bullets: [
      'Create up to 10 LinkedIn posts across available formats',
      'Turn your experience and perspectives into content',
      'Build visibility beyond your profile page',
    ],
    testid: 'deliverable-content',
  },
];

const scrollToPricing = () => {
  const el =
    document.querySelector('[data-testid="home-hero-checkout-card"]') ||
    document.getElementById('pricing');
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

const DeliverableCard = ({ d, i }) => (
  <motion.div
    {...fadeUp}
    transition={{ ...fadeUp.transition, delay: 0.05 + i * 0.05 }}
    className={`relative overflow-hidden rounded-3xl border ${d.ring} bg-card/90 backdrop-blur p-7 flex flex-col h-full`}
    data-testid={d.testid}
  >
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${d.tone}`}
    />
    <div className="relative flex flex-col h-full">
      <div data-testid={`${d.testid}-mockup`} aria-hidden="true">
        <d.Mockup />
      </div>
      <div className="mt-5 flex items-center gap-2.5">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-background/70 shadow-sm">
          <d.icon size={16} className={d.accent} strokeWidth={2} aria-hidden="true" />
        </span>
        <h3 className="text-[17.5px] font-semibold tracking-[-0.01em] text-foreground">
          {d.title}
        </h3>
      </div>
      <p className="mt-2 text-[13.5px] text-muted-foreground leading-relaxed">
        {d.body}
      </p>
      <ul className="mt-4 space-y-2.5 flex-1" data-testid={`${d.testid}-bullets`}>
        {d.bullets.map((b) => (
          <li
            key={b}
            className="flex items-start gap-2.5 text-[13px] text-foreground/85 leading-relaxed"
          >
            <Check
              size={14}
              className={`${d.accent} shrink-0 mt-0.5`}
              strokeWidth={2.4}
              aria-hidden="true"
            />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  </motion.div>
);

export const MakeoverDeliverablesSection = () => (
  <section
    className={sectionWrap}
    aria-label="What's included in the Full Profile Makeover"
    data-testid="makeover-deliverables-section"
  >
    <div className={sectionInner}>
      <motion.div {...fadeUp} className="max-w-3xl">
        <p className={kicker}>Deliverables</p>
        <h2
          className={`${displaySection} mt-4 text-3xl sm:text-4xl lg:text-5xl`}
          data-testid="deliverables-headline"
        >
          One makeover.{' '}
          <span className="text-primary">A stronger professional presence.</span>
        </h2>
        <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
          Improve the way you present yourself — from the résumé you send to
          the LinkedIn profile recruiters visit.
        </p>
      </motion.div>

      {/* Desktop grid */}
      <div
        className="mt-12 hidden md:grid gap-5 md:grid-cols-2 lg:grid-cols-4"
        data-testid="deliverables-grid"
      >
        {DELIVERABLES.map((d, i) => (
          <DeliverableCard key={d.testid} d={d} i={i} />
        ))}
      </div>

      {/* Mobile horizontal carousel (snap scroll, no deps) */}
      <div
        className="mt-10 md:hidden -mx-4 px-4"
        data-testid="deliverables-carousel"
      >
        <div
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-none pb-4"
          style={{ scrollPaddingLeft: '1rem' }}
        >
          {DELIVERABLES.map((d, i) => (
            <div
              key={d.testid}
              className="snap-start shrink-0 w-[82%]"
              data-testid={`${d.testid}-slide`}
            >
              <DeliverableCard d={d} i={i} />
            </div>
          ))}
        </div>
        <p className="mt-2 text-[11.5px] text-muted-foreground text-center">
          Swipe to see all four deliverables.
        </p>
      </div>

      <div className="mt-10 flex justify-start">
        <Button
          size="lg"
          onClick={scrollToPricing}
          className="h-12 rounded-full px-7 text-[14.5px]"
          data-testid="deliverables-cta"
        >
          Get my full makeover — ₹999
          <ArrowRight size={16} className="ml-2" aria-hidden="true" />
        </Button>
      </div>
    </div>
  </section>
);

export default MakeoverDeliverablesSection;
